import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import TopBar from '../../layouts/TopBar/topBar';
import { ChevronRightIcon, DocumentTextIcon, CodeBracketIcon, BeakerIcon, ScaleIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';

// Import the new components
import CreateFileDataset from './CreateFileDataset';
import CreateApiDataset from './CreateApiDataset';
import CreateWordPressDataset from './CreateWordPressDataset';

// Import local logos
import googleDriveLogo from '../../img/logos/new-logo-drive-google.svg';
import woocommerceLogo from '../../img/logos/woocommerce.svg';
import shopifyLogo from '../../img/logos/shopify-2.svg';
import dropboxLogo from '../../img/logos/dropbox.svg';
import notionLogo from '../../img/logos/notion-2.svg';

const CreateDataSet = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sourceType } = useParams();
  const [step, setStep] = useState(1); // 1: Source Selection, 2: Configuration
  const [selectedSourceType, setSelectedSourceType] = useState('');

  const title = step === 1 ? t('datasets.create.chooseSource') : t('datasets.create.configureSource');
  const description = step === 1 ? t('datasets.create.selectDescription') : t('datasets.create.configureDescription');

  const apiSourcePresets = useMemo(() => ([
    {
      id: 'ncbi',
      name: 'NCBI PubMed Central',
      description: 'Scientific articles and metadata from NCBI PMC.',
      icon: BeakerIcon
    },
    {
      id: 'sejm',
      name: 'Sejm / ELI API',
      description: 'Polish legal acts (Dziennik Ustaw / Monitor Polski) and legislative process data.',
      icon: ScaleIcon
    },
    {
      id: 'saos',
      name: 'SAOS',
      description: 'Polish court judgments and legal case data.',
      icon: ScaleIcon
    },
    {
      id: 'krs',
      name: 'KRS Open API',
      description: 'Company and organization records from official KRS API.',
      icon: BuildingOffice2Icon
    },
    {
      id: 'ceidg',
      name: 'CEIDG / Hurtownia Danych',
      description: 'Business registry data (requires dedicated integrator access and token).',
      icon: BuildingOffice2Icon
    },
    {
      id: 'vat',
      name: 'VAT White List',
      description: 'VAT status and account verification from MF/KAS APIs.',
      icon: BuildingOffice2Icon
    },
    {
      id: 'cellar',
      name: 'EUR-Lex / Cellar',
      description: 'EU legal documents and metadata (SPARQL / REST / RSS).',
      icon: ScaleIcon
    }
  ]), []);

  const sourceTypes = [
    {
      id: 'file',
      name: t('datasets.create.sourceTypes.file.name'),
      description: t('datasets.create.sourceTypes.file.description'),
      icon: DocumentTextIcon,
      available: true,
      logo: null
    },
    {
      id: 'external_api',
      name: t('datasets.create.sourceTypes.external_api.name'),
      description: t('datasets.create.sourceTypes.external_api.description'),
      icon: CodeBracketIcon,
      available: true,
      logo: null
    },
    ...apiSourcePresets.map((preset) => ({
      id: preset.id,
      name: preset.name,
      description: preset.description,
      icon: preset.icon,
      available: true,
      logo: null,
      isPreset: true
    })),
    // {
    //   id: 'wordpress',
    //   name: t('datasets.create.sourceTypes.wordpress.name'),
    //   description: t('datasets.create.sourceTypes.wordpress.description'),
    //   icon: null,
    //   available: true,
    //   logo: wordpressLogo
    // },
    {
      id: 'google_drive',
      name: t('datasets.create.sourceTypes.google_drive.name'),
      description: t('datasets.create.sourceTypes.google_drive.description'),
      icon: null,
      available: false,
      logo: googleDriveLogo
    },
    {
      id: 'woocommerce',
      name: t('datasets.create.sourceTypes.woocommerce.name'),
      description: t('datasets.create.sourceTypes.woocommerce.description'),
      icon: null,
      available: false,
      logo: woocommerceLogo
    },
    {
      id: 'shopify',
      name: t('datasets.create.sourceTypes.shopify.name'),
      description: t('datasets.create.sourceTypes.shopify.description'),
      icon: null,
      available: false,
      logo: shopifyLogo
    },
    {
      id: 'dropbox',
      name: t('datasets.create.sourceTypes.dropbox.name'),
      description: t('datasets.create.sourceTypes.dropbox.description'),
      icon: null,
      available: false,
      logo: dropboxLogo
    },
    {
      id: 'notion',
      name: t('datasets.create.sourceTypes.notion.name'),
      description: t('datasets.create.sourceTypes.notion.description'),
      icon: null,
      available: false,
      logo: notionLogo
    }
  ];

  useEffect(() => {
    if (!sourceType) {
      setStep(1);
      setSelectedSourceType('');
      return;
    }

    const allowed = ['file', 'wordpress', 'external_api', ...apiSourcePresets.map((preset) => preset.id)];
    if (allowed.includes(sourceType)) {
      setSelectedSourceType(sourceType);
      setStep(2);
      return;
    }

    navigate('/datasets/create-dataset', { replace: true });
  }, [sourceType, navigate, apiSourcePresets]);

  const handleSourceTypeSelect = (sourceType) => {
    navigate(`/datasets/create-dataset/${sourceType.id}`);
  };

  const handleBack = () => {
    navigate('/datasets/create-dataset');
  };

  const handleHeaderBack = () => {
    navigate('/datasets/create-dataset');
  };

  const renderConfigurationStep = () => {
    switch (selectedSourceType) {
      case 'file':
        return <CreateFileDataset onBack={handleBack} />;
      case 'external_api':
        return <CreateApiDataset onBack={handleBack} />;
      case 'wordpress':
        return <CreateWordPressDataset onBack={handleBack} />;
      case 'ncbi':
      case 'sejm':
      case 'saos':
      case 'krs':
      case 'ceidg':
      case 'vat':
      case 'cellar':
        return <CreateApiDataset onBack={handleBack} presetId={selectedSourceType} />;
      default:
        return (
          <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
            <div className="px-4 py-8 sm:p-8">
              <div className="text-center">
                <p className="text-gray-500">Configuration for this source type is not yet available.</p>
                <button
                  onClick={handleBack}
                  className="mt-4 btn btn-secondary"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="bg-neutral-100">
      {step === 2 && (
        <TopBar
          onGoBack={handleHeaderBack}
          mode="back-only"
        />
      )}
      <div className="max-w-7xl mx-auto">
        <div className="mx-auto">
          <div className="pb-12">
            <div className="mt-10 space-y-8">
              {step !== 2 ? (
                <div className="sm:flex sm:items-center mt-16">
                  <div className="sm:flex-auto">
                    <h1 className="text-3xl font-medium leading-6 text-primary-900">{title}</h1>
                    <p className="mt-4 text-base text-primary-900 opacity-60 max-w-4xl">{description}</p>
                  </div>
                </div>
              ) : (
                <div className="sm:flex sm:items-center mt-6">
                  <div className="sm:flex-auto">
                    <h1 className="text-3xl font-medium leading-6 text-primary-900">{title}</h1>
                    <p className="mt-4 text-base text-primary-900 opacity-60 max-w-4xl">{description}</p>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
                  <div className="px-4 py-8 sm:p-8">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {sourceTypes.map((sourceType) => (
                        <div
                          key={sourceType.id}
                          onClick={() => sourceType.available && handleSourceTypeSelect(sourceType)}
                          className={`relative cursor-pointer rounded-lg border p-6 hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            sourceType.available 
                              ? 'border-gray-300 bg-white hover:bg-primary-50' 
                              : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              {sourceType.logo ? (
                                <img src={sourceType.logo} alt={sourceType.name} className="h-8 w-8 object-contain" />
                              ) : (
                                <sourceType.icon className="h-8 w-8 text-gray-700" />
                              )}
                            </div>
                            <div className="ml-4 flex-1">
                              <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium text-gray-900">{sourceType.name}</h3>
                                {sourceType.premium && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                                    Premium
                                  </span>
                                )}
                                {!sourceType.available && !sourceType.premium && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Coming Soon
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-gray-500">
                                {sourceType.description}
                                {sourceType.contactUrl && sourceType.contactLabel && (
                                  <>
                                    {' '}
                                    <a
                                      href={sourceType.contactUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary-600 hover:text-primary-700 underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {sourceType.contactLabel}
                                    </a>
                                  </>
                                )}
                              </p>
                            </div>
                            {sourceType.available && (
                              <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-2" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && renderConfigurationStep()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDataSet;